import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/apiClient';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export default function Registrations() {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [openCreateUserDialog, setOpenCreateUserDialog] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [createUserForm, setCreateUserForm] = useState({
    email: '',
    password: '',
    clinic: '',
    group: '',
    abo: '',
    flics: [],
    expires: ''
  });
  const [roles, setRoles] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ open: false, registrationId: null, registrationEmail: null });

  useEffect(() => {
    fetchRegistrations();
    fetchRoles();
    fetchSubscriptions();
  }, []);

  const fetchRegistrations = async () => {
    try {
      const data = await apiClient.get('/api/registration');
      setRegistrations(data.registrations || []);
    } catch (error) {
      console.error('Error fetching registrations:', error);
      setError('Impossible de charger les inscriptions');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const data = await apiClient.get('/api/admin/roles');
      setRoles(data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const data = await apiClient.get('/api/admin/subscriptions');
      setSubscriptions(data);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    }
  };

  const updateRegistrationStatus = async (id, status) => {
    if (status === 'approved') {
      // For approval, open the user creation dialog instead of directly updating
      const registration = registrations.find(reg => reg.id === id);
      if (registration) {
        handleOpenCreateUserDialog(registration);
      }
      return;
    }

    // For rejection, update status directly
    setUpdating(id);
    try {
      await apiClient.put(`/api/registration/${id}`, { status });
      // Update the local state
      setRegistrations(prev => 
        prev.map(reg => 
          reg.id === id ? { ...reg, status } : reg
        )
      );
    } catch (error) {
      console.error('Error updating registration status:', error);
      setError('Impossible de mettre à jour le statut');
    } finally {
      setUpdating(null);
    }
  };

  const handleDeleteClick = (registration) => {
    setDeleteConfirmModal({
      open: true,
      registrationId: registration.id,
      registrationEmail: registration.email
    });
  };

  const handleDeleteConfirm = async () => {
    const { registrationId } = deleteConfirmModal;
    if (!registrationId) return;

    setDeleting(registrationId);
    setDeleteConfirmModal({ open: false, registrationId: null, registrationEmail: null });
    
    try {
      await apiClient.delete(`/api/registration/${registrationId}`);
      // Remove from local state
      setRegistrations(prev => prev.filter(reg => reg.id !== registrationId));
      setError(null);
    } catch (error) {
      console.error('Error deleting registration:', error);
      const errorMessage = error.response?.data?.error || 'Impossible de supprimer la demande d\'inscription';
      setError(errorMessage);
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmModal({ open: false, registrationId: null, registrationEmail: null });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.pending;
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      approved: 'Approuvé',
      rejected: 'Rejeté'
    };
    return labels[status] || status;
  };

  const getClinicSizeLabel = (size) => {
    const labels = {
      '1': '1 praticien',
      '2-5': '2-5 praticiens',
      '6-15': '6-15 praticiens'
    };
    return labels[size] || size;
  };

  // User creation functions
  const handleOpenCreateUserDialog = (registration) => {
    console.log('Opening create user dialog for registration:', registration);
    setSelectedRegistration(registration);
    
    
    // Calculate expiration date 30 days from now
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 30);
    const formattedExpiration = expirationDate.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\//g, '.');
    
    setCreateUserForm({
      email: registration.email || '',
      password: registration.password,
      clinic: registration.clinicName || '',
      group: '',
      abo: '',
      flics: [],
      expires: formattedExpiration
    });
    setOpenCreateUserDialog(true);
    console.log('Dialog state set to true');
  };

  const handleCloseCreateUserDialog = () => {
    setOpenCreateUserDialog(false);
    setSelectedRegistration(null);
    setCreateUserForm({
      email: '',
      password: '',
      clinic: '',
      group: '',
      abo: '',
      flics: [],
      expires: ''
    });
  };

  const handleCreateUserFormChange = (field, value) => {
    setCreateUserForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateUserAddFlic = () => {
    setCreateUserForm(prev => ({
      ...prev,
      flics: [...prev.flics, '']
    }));
  };

  const handleCreateUserRemoveFlic = (index) => {
    setCreateUserForm(prev => ({
      ...prev,
      flics: prev.flics.filter((_, i) => i !== index)
    }));
  };

  const handleCreateUserFlicChange = (index, value) => {
    setCreateUserForm(prev => ({
      ...prev,
      flics: prev.flics.map((flic, i) => i === index ? value : flic)
    }));
  };

  const validateExpirationDate = (dateString) => {
    if (!dateString || dateString.trim() === '') {
      return { isValid: true, error: null };
    }

    const dateRegex = /^(\d{2})\.(\d{2})\.(\d{4})$/;
    const match = dateString.match(dateRegex);
    
    if (!match) {
      return { 
        isValid: false, 
        error: 'Le format doit être dd.MM.yyyy (ex: 31.12.2024)' 
      };
    }

    const [, day, month, year] = match;
    const dayNum = parseInt(day, 10);
    const monthNum = parseInt(month, 10);
    const yearNum = parseInt(year, 10);

    const date = new Date(yearNum, monthNum - 1, dayNum);
    
    if (date.getDate() !== dayNum || 
        date.getMonth() !== monthNum - 1 || 
        date.getFullYear() !== yearNum) {
      return { 
        isValid: false, 
        error: 'Cette date n\'existe pas' 
      };
    }

    return { isValid: true, error: null };
  };

  const handleCreateUserSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);

      // Validate required fields
      if (!createUserForm.email || !createUserForm.password || !createUserForm.clinic || !createUserForm.group || !createUserForm.abo) {
        setError('L\'email, le mot de passe, la clinique, le groupe et l\'abonnement sont obligatoires');
        setSaving(false);
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(createUserForm.email)) {
        setError('Format d\'email invalide');
        setSaving(false);
        return;
      }

      // Validate expiration date
      const dateValidation = validateExpirationDate(createUserForm.expires);
      if (!dateValidation.isValid) {
        setError(dateValidation.error);
        setSaving(false);
        return;
      }

      const createData = {
        email: createUserForm.email,
        password: createUserForm.password,
        app_metadata: {
          clinic: createUserForm.clinic,
          group: createUserForm.group || null,
          abo: createUserForm.abo || null,
          flics: createUserForm.flics.filter(f => f.trim()).join(';') || null,
          expires: createUserForm.expires || null
        }
      };

      await apiClient.post('/api/admin/okta-users', createData);
      
      // Update registration status to approved
      if (selectedRegistration) {
        await apiClient.put(`/api/registration/${selectedRegistration.id}`, { status: 'approved' });
        
        // Update the local state
        setRegistrations(prev => 
          prev.map(reg => 
            reg.id === selectedRegistration.id ? { ...reg, status: 'approved' } : reg
          )
        );
      }
      
      handleCloseCreateUserDialog();
      setError(null);
    } catch (error) {
      console.error('Error creating user:', error);
      
      // Extract error message from response
      let errorMessage = 'Impossible de créer l\'utilisateur';
      
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        
        if (errorData && typeof errorData === 'object') {
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.details) {
            errorMessage = errorData.details;
          }
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 rounded-lg">
      <div className="px-6 py-4">
        <h2 className="text-lg font-medium text-gray-900">
          Inscriptions
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Gérez les demandes d'inscription des praticiens
        </p>
      </div>

      <div className="px-6 py-4">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Praticien
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cabinet
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Taille
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Démo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {registrations.map((registration) => (
                <tr key={registration.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(registration.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {registration.practitionerName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <a 
                      href={`mailto:${registration.email}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {registration.email}
                    </a>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div>
                      <div className="font-medium">{registration.clinicName}</div>
                      <div className="text-gray-500 text-xs">
                        {registration.clinicAddress}, {registration.clinicPostalCode} {registration.clinicCity}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getClinicSizeLabel(registration.clinicSize)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      registration.demoPreference 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {registration.demoPreference ? 'Oui' : 'Non'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(registration.status)}`}>
                      {getStatusLabel(registration.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      {registration.status === 'pending' && (
                        <>
                          <button
                            onClick={() => updateRegistrationStatus(registration.id, 'approved')}
                            disabled={updating === registration.id}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                          >
                            {updating === registration.id ? '...' : 'Approuver'}
                          </button>
                          <button
                            onClick={() => updateRegistrationStatus(registration.id, 'rejected')}
                            disabled={updating === registration.id}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                          >
                            {updating === registration.id ? '...' : 'Rejeter'}
                          </button>
                        </>
                      )}
                      {registration.status === 'approved' && (
                        <span className="text-green-600 text-xs font-medium">Approuvé</span>
                      )}
                      {registration.status === 'rejected' && (
                        <button
                          onClick={() => handleDeleteClick(registration)}
                          disabled={deleting === registration.id}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                          title="Supprimer cette demande d'inscription rejetée"
                        >
                          {deleting === registration.id ? (
                            '...'
                          ) : (
                            <>
                              <DeleteIcon className="w-4 h-4 mr-1" />
                              Supprimer
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {registrations.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500">
                    Aucune inscription trouvée
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {error && (
          <div className="mt-4 p-4 rounded-md bg-red-50 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>

      {/* Create User Dialog */}
      {console.log('Dialog state:', openCreateUserDialog)}
      {openCreateUserDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Créer un utilisateur et approuver le compte
              </h3>
              
              <form onSubmit={handleCreateUserSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {/* Email */}
                  <div>
                    <label htmlFor="create-email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="create-email"
                      value={createUserForm.email}
                      onChange={(e) => handleCreateUserFormChange('email', e.target.value)}
                      placeholder="utilisateur@exemple.com"
                      disabled={!!selectedRegistration}
                      className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm ${
                        selectedRegistration 
                          ? 'bg-gray-100 cursor-not-allowed' 
                          : 'focus:border-blue-500 focus:ring-blue-500'
                      }`}
                      required
                    />
                    <p className="mt-1 text-xs text-red-600">
                      ⚠️ Ce champ est définitif et ne pourra plus être changé
                    </p>
                  </div>

                  {/* Password */}
                  <div>
                    <label htmlFor="create-password" className="block text-sm font-medium text-gray-700 mb-1">
                      Mot de passe *
                    </label>
                    <input
                      type="password"
                      id="create-password"
                      value={createUserForm.password}
                      onChange={(e) => handleCreateUserFormChange('password', e.target.value)}
                      placeholder="Minimum 8 caractères"
                      disabled={!!selectedRegistration}
                      className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm ${
                        selectedRegistration 
                          ? 'bg-gray-100 cursor-not-allowed' 
                          : 'focus:border-blue-500 focus:ring-blue-500'
                      }`}
                      required
                    />
                    <p className="mt-1 text-xs text-red-600">
                      ⚠️ Ce champ est définitif et ne pourra plus être changé
                    </p>
                  </div>

                  {/* Clinic */}
                  <div>
                    <label htmlFor="create-clinic" className="block text-sm font-medium text-gray-700 mb-1">
                      Clinique *
                    </label>
                    <input
                      type="text"
                      id="create-clinic"
                      value={createUserForm.clinic}
                      onChange={(e) => handleCreateUserFormChange('clinic', e.target.value)}
                      placeholder="Nom de la clinique"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      required
                    />
                    <p className="mt-1 text-xs text-red-600">
                      ⚠️ Ce champ est définitif et ne pourra plus être changé
                    </p>
                  </div>

                  {/* Group Selection */}
                  <div>
                    <label htmlFor="create-group" className="block text-sm font-medium text-gray-700 mb-1">
                      Groupe *
                    </label>
                    <select
                      id="create-group"
                      value={createUserForm.group}
                      onChange={(e) => handleCreateUserFormChange('group', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      required
                    >
                      <option value="">Sélectionner un groupe</option>
                      {[...new Set(roles.map(role => role.role))].map(role => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                  </div>

                  {/* Subscription Selection */}
                  <div>
                    <label htmlFor="create-abo" className="block text-sm font-medium text-gray-700 mb-1">
                      Abonnement *
                    </label>
                    <select
                      id="create-abo"
                      value={createUserForm.abo}
                      onChange={(e) => handleCreateUserFormChange('abo', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      required
                    >
                      <option value="">Sélectionner un abonnement</option>
                      {subscriptions.map(subscription => (
                        <option key={subscription.id} value={subscription.id}>{subscription.id}</option>
                      ))}
                    </select>
                  </div>

                  {/* Expiration Date */}
                  <div>
                    <label htmlFor="create-expires" className="block text-sm font-medium text-gray-700 mb-1">
                      Date d'expiration (dd.MM.yyyy)
                    </label>
                    <input
                      type="text"
                      id="create-expires"
                      value={createUserForm.expires}
                      onChange={(e) => handleCreateUserFormChange('expires', e.target.value)}
                      placeholder="dd.MM.yyyy (laisser vide pour aucune expiration)"
                      className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                        createUserForm.expires && !validateExpirationDate(createUserForm.expires).isValid
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                      }`}
                    />
                    {createUserForm.expires && !validateExpirationDate(createUserForm.expires).isValid ? (
                      <p className="mt-1 text-xs text-red-600">
                        {validateExpirationDate(createUserForm.expires).error}
                      </p>
                    ) : (
                      <p className="mt-1 text-xs text-gray-500">
                        Format: dd.MM.yyyy (ex: 31.12.2024) ou laisser vide
                      </p>
                    )}
                  </div>
                </div>

                {/* FLICs Management */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    FLICs
                  </label>
                  <div className="space-y-2">
                    {createUserForm.flics.map((flic, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={flic}
                          onChange={(e) => handleCreateUserFlicChange(index, e.target.value)}
                          placeholder="ID FLIC"
                          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => handleCreateUserRemoveFlic(index)}
                          className="text-red-600 hover:text-red-900 p-1"
                        >
                          <DeleteIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={handleCreateUserAddFlic}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <AddIcon className="w-4 h-4 mr-1" />
                      Ajouter un FLIC
                    </button>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCloseCreateUserDialog}
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={saving || (createUserForm.expires && !validateExpirationDate(createUserForm.expires).isValid)}
                    className={`inline-flex justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 
                      ${!saving && (!createUserForm.expires || validateExpirationDate(createUserForm.expires).isValid) 
                        ? 'hover:bg-[rgb(121,192,189)]' 
                        : 'opacity-75 cursor-not-allowed'}
                      focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2`}
                  >
                    {saving ? 'Création...' : 'Créer l\'utilisateur et approuver'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmModal.open && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                <DeleteIcon className="w-6 h-6 text-red-600" />
              </div>
              
              <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                Confirmer la suppression
              </h3>
              
              <div className="mt-2 px-4 py-3">
                <p className="text-sm text-gray-500 text-center mb-4">
                  Êtes-vous sûr de vouloir supprimer cette demande d'inscription rejetée ?
                </p>
                {deleteConfirmModal.registrationEmail && (
                  <p className="text-sm font-medium text-gray-700 text-center mb-4">
                    Email : <span className="text-gray-900">{deleteConfirmModal.registrationEmail}</span>
                  </p>
                )}
                <p className="text-xs text-red-600 text-center font-medium">
                  ⚠️ Cette action est irréversible
                </p>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handleDeleteCancel}
                  className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  className="inline-flex justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
